// Copyright (c) 2025 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, FlatList, Modal, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { Redirect, useLocalSearchParams, useRootNavigationState } from 'expo-router';

import { Text } from '@/components/Themed';
import { useI18n } from '@/i18n';
import { computeStrokesReceivedForHole, parseHandicapValue } from '@/features/hole/utils';
import { useUser } from '../../user-context';
import { createAppLogger } from '@/services/appLogger';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { buildAccessHeaders, appendClaimQuery, hasClaimAccess } from '@/services/claimHttp';
import { useInviteViewer } from '@/hooks/useInviteViewer';
import { getActiveClaimSessionSync, loadClaimSession, subscribeClaimSession } from '@/services/claimSession';
import { useRoundState } from '@/stores/roundState';
import { getCurrentTourId, setCurrentTourId } from '@/services/currentTour';

type Player = {
  id: string;
  name: string;
  handicap?: number | null;
  Handicap?: number | null;
  hcp?: number | null;
  Hcp?: number | null;
  playingHandicap?: number | null;
  playing_handicap?: number | null;
  scp?: number | string | null;
  Scp?: number | string | null;
  shcp?: number | string | null;
  Shcp?: number | string | null;
};

type HoleScore = {
  playerId?: string;
  PlayerId?: string;
  holeNumber?: number;
  HoleNumber?: number;
  hole?: number;
  Hole?: number;
  par?: number;
  Par?: number;
  holePar?: number;
  HolePar?: number;
  strokeIndex?: number;
  StrokeIndex?: number;
  holeIndex?: number;
  HoleIndex?: number;
  grossStrokes?: number | null;
  GrossStrokes?: number | null;
  grossStroke?: number | null;
  GrossStroke?: number | null;
  handicapStrokes?: number | null;
  HandicapStroke?: number | null;
  handicapStroke?: number | null;
  pickup?: boolean;
  Pickup?: boolean;
};

type RoundInfo = {
  courseName?: string;
  courseAddress?: string;
  name?: string;
  description?: string;
  courseId?: string;
  CourseId?: string;
  tourId?: string | null;
  TourId?: string | null;
  createdAt?: string;
  CreatedAt?: string;
  statusUpdatedAt?: string;
  StatusUpdatedAt?: string;
};

type HoleMetaCell = {
  par?: number;
  strokeIndex?: number;
};

type RoundSummary = {
  id: string;
  courseName?: string;
  name?: string;
  description?: string;
  status?: string;
  createdAt?: string;
  statusUpdatedAt?: string;
};

type ScoreboardScore = {
  gameId?: string;
  GameId?: string;
  gameName?: string;
  GameName?: string;
  playerId?: string;
  PlayerId?: string;
  scorePoints?: number;
  ScorePoints?: number;
};

type ScoreboardResponse = {
  scores?: ScoreboardScore[];
  Scores?: ScoreboardScore[];
};

type ScoreHistoryRow = {
  playerId?: string;
  PlayerId?: string;
  gameId?: string;
  GameId?: string;
  holeNumber?: number;
  HoleNumber?: number;
  scorePoints?: number;
  ScorePoints?: number;
};

type GameDefinition = {
  id?: string;
  Id?: string;
  name?: string;
  Name?: string;
};

const HOLES = Array.from({ length: 18 }, (_, i) => i + 1);
const FRONT_NINE = HOLES.slice(0, 9);
const BACK_NINE = HOLES.slice(9);
const COL_WIDTH = 44;
const ROW_HEIGHT = 30;

// The BFF stores "n/a" for course name/address when no location was selected.
const cleanDisplayValue = (value?: string | null): string | undefined => {
  if (!value) return undefined;
  const trimmed = value.trim();
  if (!trimmed || trimmed.toLowerCase() === 'n/a') return undefined;
  return trimmed;
};

const toFiniteNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
};

const extractHoleMetaEntry = (entry: Record<string, unknown>): { hole: number; meta: HoleMetaCell } | null => {
  const holeCandidate =
    toFiniteNumber(entry.holeNumber) ??
    toFiniteNumber(entry.HoleNumber) ??
    toFiniteNumber(entry.hole) ??
    toFiniteNumber(entry.Hole) ??
    toFiniteNumber(entry.number) ??
    toFiniteNumber(entry.Number);
  const hole = holeCandidate ? Math.trunc(holeCandidate) : 0;
  if (!hole || hole < 1 || hole > 18) return null;

  const par =
    toFiniteNumber(entry.par) ??
    toFiniteNumber(entry.Par) ??
    toFiniteNumber(entry.holePar) ??
    toFiniteNumber(entry.HolePar);
  const strokeIndex =
    toFiniteNumber(entry.strokeIndex) ??
    toFiniteNumber(entry.StrokeIndex) ??
    toFiniteNumber(entry.holeIndex) ??
    toFiniteNumber(entry.HoleIndex) ??
    toFiniteNumber(entry.index) ??
    toFiniteNumber(entry.Index);

  if (par == null && strokeIndex == null) return null;
  return {
    hole,
    meta: {
      par: par == null ? undefined : Math.trunc(par),
      strokeIndex: strokeIndex == null ? undefined : Math.trunc(strokeIndex),
    },
  };
};

const extractHoleMetaFromRoundPayload = (payload: unknown): Map<number, HoleMetaCell> => {
  const meta = new Map<number, HoleMetaCell>();
  if (!payload || typeof payload !== 'object') return meta;
  const obj = payload as Record<string, unknown>;

  const applyEntry = (entry: Record<string, unknown>) => {
    const normalized = extractHoleMetaEntry(entry);
    if (!normalized) return;
    const existing = meta.get(normalized.hole) ?? {};
    meta.set(normalized.hole, {
      par: existing.par ?? normalized.meta.par,
      strokeIndex: existing.strokeIndex ?? normalized.meta.strokeIndex,
    });
  };

  const applySource = (source: unknown) => {
    if (Array.isArray(source)) {
      source.forEach((item) => {
        if (item && typeof item === 'object') {
          applyEntry(item as Record<string, unknown>);
        }
      });
      return;
    }
    if (source && typeof source === 'object') {
      Object.entries(source as Record<string, unknown>).forEach(([key, value]) => {
        if (!value || typeof value !== 'object') return;
        const item = value as Record<string, unknown>;
        applyEntry({ holeNumber: key, ...item });
      });
    }
  };

  applySource(obj.holes);
  applySource(obj.Holes);
  applySource(obj.holeInfos);
  applySource(obj.HoleInfos);
  applySource(obj.holeMeta);
  applySource(obj.HoleMeta);
  if (obj.course && typeof obj.course === 'object') {
    const course = obj.course as Record<string, unknown>;
    applySource(course.holes);
    applySource(course.Holes);
    applySource(course.holeInfos);
    applySource(course.HoleInfos);
    applySource(course.holeMeta);
    applySource(course.HoleMeta);
  }
  if (obj.Course && typeof obj.Course === 'object') {
    const course = obj.Course as Record<string, unknown>;
    applySource(course.holes);
    applySource(course.Holes);
    applySource(course.holeInfos);
    applySource(course.HoleInfos);
    applySource(course.holeMeta);
    applySource(course.HoleMeta);
  }

  return meta;
};

const mergeHoleMeta = (primary: Map<number, HoleMetaCell>, fallback: Map<number, HoleMetaCell>): Map<number, HoleMetaCell> => {
  const merged = new Map<number, HoleMetaCell>();
  HOLES.forEach((hole) => {
    const a = primary.get(hole);
    const b = fallback.get(hole);
    if (!a && !b) return;
    merged.set(hole, {
      par: a?.par ?? b?.par,
      strokeIndex: a?.strokeIndex ?? b?.strokeIndex,
    });
  });
  return merged;
};

export default function ScorecardScreen() {
  const { t } = useI18n();
  const { user } = useUser();
  const navState = useRootNavigationState();
  const isNavigationReady = !!navState?.key;
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const { tourId: tourParam, roundId: roundIdParam } = useLocalSearchParams<{ tourId?: string; roundId?: string }>();
  const roundId = useRoundState((state) => state.roundId);
  const setRoundId = useRoundState((state) => state.setRoundId);
  const setRoundStatus = useRoundState((state) => state.setRoundStatus);
  const [claimSession, setClaimSession] = useState(() => getActiveClaimSessionSync());
  const [claimSessionLoaded, setClaimSessionLoaded] = useState(false);
  const [tourId, setTourId] = useState<string | null>(null);
  const [players, setPlayers] = useState<Player[]>([]);
  const [scores, setScores] = useState<HoleScore[]>([]);
  const [scoreboardScores, setScoreboardScores] = useState<ScoreboardScore[]>([]);
  const [scoreHistoryRows, setScoreHistoryRows] = useState<ScoreHistoryRow[]>([]);
  const [gameDefinitions, setGameDefinitions] = useState<GameDefinition[]>([]);
  const [roundGameDefinitionIds, setRoundGameDefinitionIds] = useState<string[]>([]);
  const [roundInfo, setRoundInfo] = useState<RoundInfo | null>(null);
  const [rounds, setRounds] = useState<RoundSummary[]>([]);
  const [roundsLoading, setRoundsLoading] = useState(false);
  const [roundsError, setRoundsError] = useState<string | null>(null);
  const [roundPickerOpen, setRoundPickerOpen] = useState(false);
  const [roundHoleMeta, setRoundHoleMeta] = useState<Map<number, HoleMetaCell>>(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasGuestClaimSessionAccess = !!claimSession?.claimClientId && (
    hasClaimAccess(claimSession) ||
    !!claimSession?.inviteToken?.trim()
  );
  const isGuestClaimMode = !user?.token && hasGuestClaimSessionAccess;
  // Invite viewers: no claim session, just an invite token in the URL. Kept separate from
  // isGuestClaimMode so neither kind of anonymous visitor inherits the other's affordances.
  const { isInviteViewer, accessSession: viewerAccessSession } = useInviteViewer();
  const anonymousReadAllowed = isGuestClaimMode || isInviteViewer;
  const accessSession = claimSession ?? viewerAccessSession;
  const logToBff = useMemo(
    () =>
      createAppLogger(apiBaseUrl, user?.token, {
        source: 'scorecard',
      }),
    [apiBaseUrl, user?.token]
  );

  useEffect(() => {
    let cancelled = false;
    loadClaimSession()
      .catch(() => null)
      .finally(() => {
        if (!cancelled) {
          setClaimSession(getActiveClaimSessionSync());
          setClaimSessionLoaded(true);
        }
      });
    const unsubscribe = subscribeClaimSession((nextClaimSession) => {
      if (cancelled) {
        return;
      }

      setClaimSession(nextClaimSession);
      setClaimSessionLoaded(true);
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  // The round's own tour id (from the round payload) beats the localStorage-remembered
  // current tour: that storage is shared across browser windows, so another window on a
  // different tour would otherwise make this screen list that tour's rounds.
  const roundTourId = roundInfo?.tourId ?? roundInfo?.TourId ?? null;

  useEffect(() => {
    if (!isNavigationReady) return;
    const resolvedTour = tourParam ?? roundTourId ?? getCurrentTourId() ?? claimSession?.tourId ?? null;
    setTourId(resolvedTour ?? null);
    if (resolvedTour) {
      setCurrentTourId(resolvedTour, { preserveRound: true });
    }
  }, [claimSession?.tourId, isNavigationReady, roundTourId, tourParam]);

  useEffect(() => {
    const resolvedRound =
      (Array.isArray(roundIdParam) ? roundIdParam[0] : roundIdParam) ??
      claimSession?.startedRoundId ??
      null;
    if (resolvedRound && resolvedRound !== roundId) {
      setRoundId(resolvedRound);
    }
  }, [claimSession?.startedRoundId, roundId, roundIdParam, setRoundId]);

  useEffect(() => {
    const selectedRound = rounds.find((candidate) => candidate.id === roundId) ?? null;
    setRoundStatus(selectedRound?.status ?? null);
  }, [roundId, rounds, setRoundStatus]);

  useEffect(() => {
    if (!tourId || !isNavigationReady || (!user?.token && !anonymousReadAllowed)) return;
    const loadRounds = async () => {
      setRoundsLoading(true);
      setRoundsError(null);
      try {
        const response = await fetch(appendClaimQuery(`${apiBaseUrl}/api/rounds/tour/${tourId}`, accessSession), {
          headers: buildAccessHeaders(user?.token),
        });
        if (!response.ok) {
          throw new Error(`${t('scorecard.selectRoundError')} (${response.status})`);
        }
        const payload = (await response.json()) as RoundSummary[];
        setRounds(payload);
      } catch (err) {
        setRoundsError(t('scorecard.selectRoundError'));
        logToBff('scorecard.rounds.error', { tourId, message: err instanceof Error ? err.message : String(err) }, 'warn');
      } finally {
        setRoundsLoading(false);
      }
    };
    loadRounds();
  }, [accessSession, apiBaseUrl, anonymousReadAllowed, isNavigationReady, logToBff, t, tourId, user?.token]);

  useEffect(() => {
    if (!roundId || !isNavigationReady || (!user?.token && !anonymousReadAllowed)) return;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const headers = buildAccessHeaders(user?.token);
        const [playersRes, scoresRes, roundRes, scoreboardRes, scoreHistoryRes, gameDefsRes] = await Promise.all([
          fetch(appendClaimQuery(`${apiBaseUrl}/api/rounds/${roundId}/players`, accessSession), {
            headers,
          }),
          fetch(appendClaimQuery(`${apiBaseUrl}/api/rounds/${roundId}/hole-entries`, accessSession), {
            headers,
          }),
          fetch(appendClaimQuery(`${apiBaseUrl}/api/rounds/${roundId}`, accessSession), {
            headers,
          }),
          fetch(appendClaimQuery(`${apiBaseUrl}/api/rounds/${roundId}/scoreboard`, accessSession), {
            headers,
          }),
          fetch(appendClaimQuery(`${apiBaseUrl}/api/rounds/${roundId}/score-history`, accessSession), {
            headers,
          }),
          fetch(appendClaimQuery(`${apiBaseUrl}/api/game-definitions`, accessSession), {
            headers,
          }),
        ]);

        if (!playersRes.ok) {
          throw new Error(`${t('scorecard.loadError')} (${playersRes.status})`);
        }
        if (!scoresRes.ok) {
          throw new Error(`${t('scorecard.loadError')} (${scoresRes.status})`);
        }

        const [playerPayload, scorePayload, roundPayload, scoreboardPayload, scoreHistoryPayload, gameDefsPayload] = await Promise.all([
          playersRes.json(),
          scoresRes.json(),
          roundRes.ok ? roundRes.json() : Promise.resolve(null),
          scoreboardRes.ok ? scoreboardRes.json() : Promise.resolve({ scores: [] }),
          scoreHistoryRes.ok ? scoreHistoryRes.json() : Promise.resolve([]),
          gameDefsRes.ok ? gameDefsRes.json() : Promise.resolve([]),
        ]);

        const playerData = playerPayload as { players?: Player[]; Players?: Player[] };
        const scoreboardData = scoreboardPayload as ScoreboardResponse;
        const normalizedScoreboard = Array.isArray(scoreboardData.scores)
          ? scoreboardData.scores
          : Array.isArray(scoreboardData.Scores)
            ? scoreboardData.Scores
            : [];
        const roundData = roundPayload as {
          gameDefinitionIds?: string[];
          GameDefinitionIds?: string[];
        } | null;
        const resolvedRoundGameDefinitionIds = Array.isArray(roundData?.gameDefinitionIds)
          ? roundData?.gameDefinitionIds
          : Array.isArray(roundData?.GameDefinitionIds)
            ? roundData?.GameDefinitionIds
            : [];
        const rawPlayers = playerData.players ?? playerData.Players ?? [];
        const normalizedPlayers = rawPlayers.map((player) => {
          const handicap = parseHandicapValue(
            player.handicap ??
            player.Handicap ??
            player.hcp ??
            player.Hcp ??
            player.playingHandicap ??
            player.playing_handicap
          );
          // The round-specific playing handicap (BFF sends it as scp/shcp), shown in the scorecard header.
          const playingHandicap =
            parseHandicapValue(
              player.shcp ??
              player.Shcp ??
              player.scp ??
              player.Scp ??
              player.playingHandicap ??
              player.playing_handicap
            ) ?? handicap;
          return { ...player, handicap, playingHandicap };
        });
        setPlayers(normalizedPlayers);
        setScores((scorePayload as HoleScore[]) ?? []);
        const normalizedRoundInfo = roundPayload as RoundInfo | null;
        setRoundInfo(normalizedRoundInfo);
        setRoundStatus(((roundPayload as { status?: string } | null)?.status) ?? null);
        const roundMeta = extractHoleMetaFromRoundPayload(roundPayload);
        let resolvedMeta = roundMeta;
        const roundCourseId =
          (normalizedRoundInfo?.courseId ?? normalizedRoundInfo?.CourseId ?? '').toString().trim();
        if (resolvedMeta.size < HOLES.length && roundCourseId) {
          try {
            const courseResponse = await fetch(appendClaimQuery(`${apiBaseUrl}/api/geodata/courses/${roundCourseId}`, accessSession), {
              headers,
            });
            if (courseResponse.ok) {
              const coursePayload = await courseResponse.json();
              const courseMeta = extractHoleMetaFromRoundPayload(coursePayload);
              resolvedMeta = mergeHoleMeta(resolvedMeta, courseMeta);
            }
          } catch {
            // Keep scorecard working with whatever round/hole-entry metadata is available.
          }
        }
        setRoundHoleMeta(resolvedMeta);
        setScoreboardScores(normalizedScoreboard);
        setScoreHistoryRows((scoreHistoryPayload as ScoreHistoryRow[]) ?? []);
        setGameDefinitions((gameDefsPayload as GameDefinition[]) ?? []);
        setRoundGameDefinitionIds(resolvedRoundGameDefinitionIds);
        logToBff('scorecard.loaded', { roundId });
      } catch (err) {
        setError(t('scorecard.loadError'));
        logToBff('scorecard.load.error', { roundId, message: err instanceof Error ? err.message : String(err) }, 'warn');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [accessSession, apiBaseUrl, anonymousReadAllowed, isNavigationReady, logToBff, roundId, t, user?.token]);

  const scoreMap = new Map<string, HoleScore>();
  scores.forEach((score) => {
    const playerId = (score.playerId ?? score.PlayerId)?.toString() ?? '';
    const hole = score.holeNumber ?? score.HoleNumber ?? score.hole ?? score.Hole ?? 0;
    if (!playerId || !hole) return;
    scoreMap.set(`${playerId}:${hole}`, score);
  });

  const holeMeta = new Map<number, { par?: number; strokeIndex?: number }>();
  roundHoleMeta.forEach((meta, hole) => {
    holeMeta.set(hole, { par: meta.par, strokeIndex: meta.strokeIndex });
  });
  scores.forEach((score) => {
    const hole = score.holeNumber ?? score.HoleNumber ?? score.hole ?? score.Hole ?? 0;
    if (!hole) return;
    const existing = holeMeta.get(hole) ?? {};
    const fallbackPar =
      score.par ??
      score.Par ??
      score.holePar ??
      score.HolePar;
    const fallbackStrokeIndex =
      score.strokeIndex ??
      score.StrokeIndex ??
      score.holeIndex ??
      score.HoleIndex;
    if (existing.par == null && existing.strokeIndex == null && fallbackPar == null && fallbackStrokeIndex == null) {
      return;
    }
    holeMeta.set(hole, {
      par: existing.par ?? fallbackPar,
      strokeIndex: existing.strokeIndex ?? fallbackStrokeIndex,
    });
  });

  const renderStrokes = (player: Player, hole: number) => {
    const playerId = player.id;
    const score = scoreMap.get(`${playerId}:${hole}`);
    const holeIndex = holeMeta.get(hole)?.strokeIndex ?? null;
    const playerHandicap = parseHandicapValue(
      player.handicap ??
      player.Handicap ??
      player.hcp ??
      player.Hcp ??
      player.playingHandicap ??
      player.playing_handicap
    );
    const fallbackHandicapStrokes = computeStrokesReceivedForHole(playerHandicap ?? null, holeIndex);
    if (!score) {
      if (!fallbackHandicapStrokes || fallbackHandicapStrokes <= 0) return <Text style={styles.strokeValue}>-</Text>;
      return (
        <View style={styles.strokeCell}>
          <Text style={styles.strokeValue}>-</Text>
          <Text style={styles.handicapText}>+{fallbackHandicapStrokes}</Text>
        </View>
      );
    }
    const pickup = score.pickup ?? score.Pickup;
    if (pickup) return <Text style={styles.strokeValue}>X</Text>;
    const strokes = score.grossStrokes ?? score.GrossStrokes ?? score.grossStroke ?? score.GrossStroke;
    const rawHandicap =
      score.handicapStroke ??
      score.HandicapStroke ??
      score.handicapStrokes ??
      score.HandicapStroke ??
      fallbackHandicapStrokes;
    const handicap = typeof rawHandicap === 'number' && Number.isFinite(rawHandicap)
      ? (rawHandicap > 0 ? rawHandicap : fallbackHandicapStrokes)
      : fallbackHandicapStrokes;
    if (strokes == null) return <Text style={styles.strokeValue}>-</Text>;
    if (!handicap || handicap <= 0) return <Text style={styles.strokeValue}>{String(strokes)}</Text>;
    return (
      <View style={styles.strokeCell}>
        <Text style={styles.strokeValue}>{strokes}</Text>
        <Text style={styles.handicapText}>+{handicap}</Text>
      </View>
    );
  };

  const stablefordGameIds = useMemo(() => {
    const ids = new Set<string>();

    scoreboardScores
      .filter((score) => {
        const gameName = (score.gameName ?? score.GameName ?? '').toString().toLowerCase();
        return gameName.includes('stableford');
      })
      .map((score) => (score.gameId ?? score.GameId ?? '').toString())
      .filter((id) => id.length > 0)
      .forEach((id) => ids.add(id));

    const gameDefinitionNameById = new Map<string, string>();
    gameDefinitions.forEach((definition) => {
      const id = (definition.id ?? definition.Id ?? '').toString();
      const name = (definition.name ?? definition.Name ?? '').toString().toLowerCase();
      if (id) {
        gameDefinitionNameById.set(id, name);
      }
    });
    roundGameDefinitionIds.forEach((id) => {
      const name = gameDefinitionNameById.get(id);
      if (name?.includes('stableford')) {
        ids.add(id);
      }
    });

    return ids;
  }, [gameDefinitions, roundGameDefinitionIds, scoreboardScores]);

  // score-history rows carry the per-hole Stableford points for each player/hole, so use them directly.
  const stablefordPointsByPlayerHole = useMemo(() => {
    const map = new Map<string, number>();
    scoreHistoryRows.forEach((row) => {
      const gameId = (row.gameId ?? row.GameId ?? '').toString();
      const playerId = (row.playerId ?? row.PlayerId ?? '').toString();
      const hole = row.holeNumber ?? row.HoleNumber ?? 0;
      const scorePoints = row.scorePoints ?? row.ScorePoints;
      if (!stablefordGameIds.has(gameId) || !playerId || !hole || scorePoints == null) return;
      map.set(`${playerId}:${hole}`, Number(scorePoints));
    });
    return map;
  }, [scoreHistoryRows, stablefordGameIds]);

  const stablefordTotals = useMemo(() => {
    const totals = new Map<string, number>();
    scoreboardScores.forEach((score) => {
      const gameId = (score.gameId ?? score.GameId ?? '').toString();
      const playerId = (score.playerId ?? score.PlayerId ?? '').toString();
      const scorePoints = score.scorePoints ?? score.ScorePoints;
      if (!stablefordGameIds.has(gameId) || !playerId || scorePoints == null) return;
      totals.set(playerId, (totals.get(playerId) ?? 0) + Number(scorePoints));
    });
    return totals;
  }, [scoreboardScores, stablefordGameIds]);

  const renderStableford = (playerId: string, hole: number) => {
    const backendPoints = stablefordPointsByPlayerHole.get(`${playerId}:${hole}`);
    return backendPoints == null ? '-' : backendPoints;
  };

  const sumGrossForHoles = (playerId: string, holes: number[]): number => {
    let total = 0;
    holes.forEach((hole) => {
      const score = scoreMap.get(`${playerId}:${hole}`);
      const pickup = score?.pickup ?? score?.Pickup;
      const strokes = score?.grossStrokes ?? score?.GrossStrokes ?? score?.grossStroke ?? score?.GrossStroke;
      if (!pickup && strokes != null) {
        total += strokes;
      }
    });
    return total;
  };

  const sumStablefordForHoles = (playerId: string, holes: number[]): number | null => {
    let total = 0;
    let hasPoints = false;
    holes.forEach((hole) => {
      const points = stablefordPointsByPlayerHole.get(`${playerId}:${hole}`);
      if (points != null) {
        total += points;
        hasPoints = true;
      }
    });
    return hasPoints ? total : null;
  };

  const sumParForHoles = (holes: number[]): number | null => {
    let total = 0;
    let hasPar = false;
    holes.forEach((hole) => {
      const par = holeMeta.get(hole)?.par;
      if (par != null) {
        total += par;
        hasPar = true;
      }
    });
    return hasPar ? total : null;
  };

  const renderFixedHoleRows = (holes: number[]) =>
    holes.map((hole) => {
      const meta = holeMeta.get(hole);
      return (
        <View key={`fixed-row-${hole}`} style={styles.row}>
          <Text style={[styles.cell, styles.holeCell]}>{hole}</Text>
          <Text style={[styles.cell, styles.metaCell]}>{meta?.par ?? '-'}</Text>
          <Text style={[styles.cell, styles.metaCell]}>{meta?.strokeIndex ?? '-'}</Text>
        </View>
      );
    });

  const renderPlayerHoleRows = (holes: number[]) =>
    holes.map((hole) => (
      <View key={`player-row-${hole}`} style={styles.row}>
        {players.map((player) => (
          <View key={`${player.id}-${hole}`} style={[styles.playerCells, styles.playerSeparator]}>
            <View style={[styles.cell, styles.scoreCell, styles.scoreCellContainer]}>{renderStrokes(player, hole)}</View>
            <Text style={[styles.cell, styles.scoreCell]}>{renderStableford(player.id, hole)}</Text>
          </View>
        ))}
      </View>
    ));

  const renderFixedSubtotalRow = (key: string, label: string, holes: number[]) => (
    <View key={`fixed-subtotal-${key}`} style={[styles.row, styles.summaryRow]}>
      <Text style={[styles.cell, styles.holeCell]}>{label}</Text>
      <Text style={[styles.cell, styles.metaCell]}>{sumParForHoles(holes) ?? '-'}</Text>
      <Text style={[styles.cell, styles.metaCell]}>-</Text>
    </View>
  );

  const renderPlayerSubtotalRow = (key: string, holes: number[]) => (
    <View key={`player-subtotal-${key}`} style={[styles.row, styles.summaryRow]}>
      {players.map((player) => (
        <View key={`${player.id}-${key}`} style={[styles.playerCells, styles.playerSeparator]}>
          <Text style={[styles.cell, styles.scoreCell]}>{sumGrossForHoles(player.id, holes)}</Text>
          <Text style={[styles.cell, styles.scoreCell]}>{sumStablefordForHoles(player.id, holes) ?? '-'}</Text>
        </View>
      ))}
    </View>
  );

  const selectedRound = rounds.find((round) => round.id === roundId) ?? null;

  const roundTimestamp =
    selectedRound?.statusUpdatedAt ??
    selectedRound?.createdAt ??
    roundInfo?.statusUpdatedAt ??
    roundInfo?.StatusUpdatedAt ??
    roundInfo?.createdAt ??
    roundInfo?.CreatedAt ??
    null;
  let roundDateLabel: string | null = null;
  if (roundTimestamp) {
    const parsedDate = new Date(roundTimestamp);
    if (!Number.isNaN(parsedDate.getTime())) {
      roundDateLabel = parsedDate.toLocaleDateString(undefined, { day: '2-digit', month: 'short' });
    }
  }

  if (!isNavigationReady) return null;
  if (!user && !isInviteViewer && !claimSessionLoaded) return null;
  if (!user && !anonymousReadAllowed && !roundId) return <Redirect href="/" />;

  if (!roundId) {
    return (
      <View style={styles.container}>
        <Text style={styles.title}>{t('scorecard.title')}</Text>
        {tourId ? (
          <>
            <Text style={styles.meta}>{t('scorecard.selectRoundTitle')}</Text>
            {roundsLoading ? <Text style={styles.meta}>{t('scorecard.selectRoundLoading')}</Text> : null}
            {roundsError ? <Text style={styles.error}>{roundsError}</Text> : null}
            {rounds.length === 0 && !roundsLoading ? <Text style={styles.meta}>{t('scorecard.selectRoundEmpty')}</Text> : null}
            {rounds.map((round) => {
              const status = String(round.status ?? '-').trim() || '-';
              return (
                <View key={round.id} style={styles.roundSelectRow}>
                  <Text style={styles.roundSelectName} numberOfLines={1}>
                    {cleanDisplayValue(round.name) ?? cleanDisplayValue(round.courseName) ?? t('scorecard.selectRoundFallback')}
                  </Text>
                  <Text style={styles.roundSelectMeta}>{status}</Text>
                  <Text style={styles.roundSelectLink} onPress={() => setRoundId(round.id)}>
                    {t('scorecard.selectRound')}
                  </Text>
                </View>
              );
            })}
          </>
        ) : (
          <Text style={styles.meta}>{t('scorecard.missingRound')}</Text>
        )}
      </View>
    );
  }

  return (
    <ScrollView contentContainerStyle={styles.container}>
      <Text style={styles.title}>
        {t('scorecard.title')}
        {roundDateLabel ? <Text style={styles.titleDate}>{`  ${roundDateLabel}`}</Text> : null}
      </Text>
      {tourId && rounds.length ? (
        <>
          <Text style={styles.meta}>{t('scorecard.selectRoundTitle')}</Text>
          <Pressable style={styles.roundPicker} onPress={() => setRoundPickerOpen(true)}>
            <Text style={styles.roundPickerText} numberOfLines={1}>
              {cleanDisplayValue(selectedRound?.name) ??
                cleanDisplayValue(selectedRound?.courseName) ??
                t('scorecard.selectRoundFallback')}
            </Text>
            <Text style={styles.roundPickerHint}>{t('scorecard.selectRound')}</Text>
          </Pressable>
          <Modal visible={roundPickerOpen} transparent animationType="fade" onRequestClose={() => setRoundPickerOpen(false)}>
            <Pressable style={styles.modalBackdrop} onPress={() => setRoundPickerOpen(false)}>
              <View style={styles.modalCard}>
                <FlatList
                  data={rounds}
                  keyExtractor={(item) => item.id}
                  renderItem={({ item }) => {
                    const status = String(item.status ?? '-').trim() || '-';
                    return (
                      <Pressable
                        style={[
                          styles.roundOption,
                          item.id === roundId && styles.roundOptionActive,
                        ]}
                        onPress={() => {
                          setRoundId(item.id);
                          setRoundPickerOpen(false);
                        }}
                      >
                        <Text style={styles.roundOptionTitle}>
                          {cleanDisplayValue(item.name) ?? cleanDisplayValue(item.courseName) ?? t('scorecard.selectRoundFallback')}
                        </Text>
                        <Text style={styles.roundOptionSubtitle}>{status}</Text>
                      </Pressable>
                    );
                  }}
                  ItemSeparatorComponent={() => <View style={styles.separator} />}
                />
              </View>
            </Pressable>
          </Modal>
        </>
      ) : null}
      {cleanDisplayValue(roundInfo?.name) ?? cleanDisplayValue(roundInfo?.courseName) ? (
        <Text style={styles.meta}>{cleanDisplayValue(roundInfo?.name) ?? cleanDisplayValue(roundInfo?.courseName)}</Text>
      ) : null}
      {cleanDisplayValue(roundInfo?.courseAddress) ? (
        <Text style={styles.meta}>{cleanDisplayValue(roundInfo?.courseAddress)}</Text>
      ) : null}
      {loading ? <ActivityIndicator color="#d5e6ff" /> : null}
      {error ? <Text style={styles.error}>{error}</Text> : null}
      <View style={styles.tableWrapper}>
        <View style={styles.fixedColumns}>
          <View style={styles.headerRow}>
            <Text style={[styles.cell, styles.holeCell]}>{t('scorecard.hole')}</Text>
            <Text style={[styles.cell, styles.metaCell]}>{t('scorecard.par')}</Text>
            <Text style={[styles.cell, styles.metaCell]}>{t('scorecard.index')}</Text>
          </View>
          <View style={styles.subHeaderRow}>
            <View style={styles.subHeaderLeftSpacer} />
          </View>
          {renderFixedHoleRows(FRONT_NINE)}
          {renderFixedSubtotalRow('out', t('scorecard.out'), FRONT_NINE)}
          {renderFixedHoleRows(BACK_NINE)}
          {renderFixedSubtotalRow('in', t('scorecard.in'), BACK_NINE)}
          <View style={[styles.row, styles.summaryRow]}>
            <Text style={[styles.cell, styles.holeCell]}>{t('scorecard.total')}</Text>
            <Text style={[styles.cell, styles.metaCell]}>{sumParForHoles(HOLES) ?? '-'}</Text>
            <Text style={[styles.cell, styles.metaCell]}>-</Text>
          </View>
        </View>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.playersScroll}>
          <View>
            <View style={styles.headerRow}>
              {players.map((player) => (
                <View key={player.id} style={[styles.playerCells, styles.playerSeparator]}>
                  <Text style={[styles.cell, styles.playerHeaderCell]} numberOfLines={1}>
                    {player.name}
                    {player.playingHandicap != null ? (
                      <Text style={styles.playerHcpText}>{` (${player.playingHandicap})`}</Text>
                    ) : null}
                  </Text>
                </View>
              ))}
            </View>
            <View style={styles.subHeaderRow}>
              {players.map((player) => (
                <View key={`${player.id}-labels`} style={[styles.playerSubHeader, styles.playerSeparator]}>
                  <Text style={[styles.cell, styles.scoreCell, styles.subHeaderText]}>{t('scorecard.strokes')}</Text>
                  <Text style={[styles.cell, styles.scoreCell, styles.subHeaderText]}>{t('scorecard.points')}</Text>
                </View>
              ))}
            </View>
            {renderPlayerHoleRows(FRONT_NINE)}
            {renderPlayerSubtotalRow('out', FRONT_NINE)}
            {renderPlayerHoleRows(BACK_NINE)}
            {renderPlayerSubtotalRow('in', BACK_NINE)}
            <View style={[styles.row, styles.summaryRow]}>
              {players.map((player) => (
                <View key={`${player.id}-total`} style={[styles.playerCells, styles.playerSeparator]}>
                  <Text style={[styles.cell, styles.scoreCell]}>{sumGrossForHoles(player.id, HOLES)}</Text>
                  <Text style={[styles.cell, styles.scoreCell]}>
                    {stablefordTotals.has(player.id) ? stablefordTotals.get(player.id) : '-'}
                  </Text>
                </View>
              ))}
            </View>
          </View>
        </ScrollView>
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    padding: 20,
    gap: 12,
    backgroundColor: '#ffffff',
  },
  title: {
    fontSize: 22,
    fontWeight: '700',
    color: '#0b0b0b',
  },
  titleDate: {
    fontSize: 14,
    fontWeight: '400',
    color: '#4a4a4a',
  },
  meta: {
    color: '#4a4a4a',
  },
  error: {
    color: '#ff8c8c',
  },
  roundPicker: {
    borderWidth: 1,
    borderColor: '#d2d6dc',
    borderRadius: 10,
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: '#ffffff',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  roundPickerText: {
    fontSize: 14,
    fontWeight: '700',
    color: '#1b1b1b',
    flex: 1,
  },
  roundPickerHint: {
    fontSize: 12,
    color: '#1c4ed8',
    fontWeight: '700',
    marginLeft: 12,
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    padding: 20,
    justifyContent: 'center',
  },
  modalCard: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 12,
    maxHeight: '80%',
  },
  roundOption: {
    paddingVertical: 10,
    paddingHorizontal: 8,
  },
  roundOptionActive: {
    backgroundColor: '#eef2ff',
    borderRadius: 8,
  },
  roundOptionTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: '#1b1b1b',
  },
  roundOptionSubtitle: {
    fontSize: 12,
    color: '#4a4a4a',
  },
  separator: {
    height: 1,
    backgroundColor: '#e5e7eb',
  },
  roundSelectRow: {
    borderWidth: 1,
    borderColor: '#d2d6dc',
    borderRadius: 10,
    paddingVertical: 8,
    paddingHorizontal: 10,
    backgroundColor: '#ffffff',
  },
  roundSelectName: {
    fontSize: 14,
    fontWeight: '700',
    color: '#1b1b1b',
  },
  roundSelectMeta: {
    fontSize: 12,
    color: '#4a4a4a',
  },
  roundSelectLink: {
    fontSize: 12,
    color: '#1c4ed8',
    fontWeight: '700',
    marginTop: 4,
  },
  tableWrapper: {
    flexDirection: 'row',
    paddingVertical: 6,
  },
  fixedColumns: {
    borderRightWidth: 2,
    borderRightColor: '#4b5563',
  },
  playersScroll: {
    flexGrow: 1,
    flexShrink: 1,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    height: ROW_HEIGHT,
    backgroundColor: '#f1f3f6',
  },
  subHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    height: ROW_HEIGHT,
    backgroundColor: '#f8f9fb',
  },
  subHeaderLeftSpacer: {
    width: COL_WIDTH * 3,
    height: '100%',
    borderLeftWidth: 1,
    borderLeftColor: '#d2d6dc',
    backgroundColor: '#f8f9fb',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    height: ROW_HEIGHT,
    backgroundColor: '#ffffff',
  },
  summaryRow: {
    backgroundColor: '#f7f7f9',
  },
  cell: {
    width: COL_WIDTH,
    height: ROW_HEIGHT,
    textAlign: 'center',
    color: '#121212',
    fontSize: 12,
    paddingVertical: 6,
    borderWidth: 1,
    borderColor: '#d2d6dc',
  },
  holeCell: {
    width: COL_WIDTH,
    fontWeight: '700',
  },
  metaCell: {
    width: COL_WIDTH,
  },
  playerHeaderCell: {
    width: COL_WIDTH * 2,
    fontWeight: '700',
  },
  playerHcpText: {
    fontSize: 10,
    fontWeight: '400',
    color: '#4a4a4a',
  },
  playerSubHeader: {
    flexDirection: 'row',
  },
  playerCells: {
    flexDirection: 'row',
  },
  playerSeparator: {
    borderLeftWidth: 2,
    borderLeftColor: '#4b5563',
  },
  scoreCell: {
    width: COL_WIDTH,
  },
  scoreCellContainer: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  strokeCell: {
    position: 'relative',
    alignItems: 'center',
    justifyContent: 'center',
    width: COL_WIDTH,
    height: '100%',
  },
  strokeValue: {
    fontSize: 12,
    color: '#121212',
  },
  subHeaderText: {
    fontSize: 10,
    fontWeight: '700',
    color: '#111827',
  },
  handicapText: {
    position: 'absolute',
    top: 0,
    right: 2,
    fontSize: 8,
    color: '#1f2937',
  },
});
